<template>
  <div class="lecturer-page flex flex-col relative">
    <!-- Header / Facebook-style -->
    <header class="facebook-header shadow-lg">
      <!-- Cover image if available -->
      <div
        v-if="hasCover"
        class="cover-image"
        :style="heroBackgroundStyle"
      ></div>
      <!-- Profile info -->
      <div class="profile-info container mx-auto flex items-center p-4">
        <img
          v-if="lecturer?.avatar"
          :src="lecturer.avatar"
          alt="Lecturer Avatar"
          class="rounded-full w-20 h-20 mr-4 border-2 border-gray-300 object-cover"
        />
        <div>
          <h1 class="text-2xl font-bold">{{ lecturer?.name }}</h1>
          <div
            v-if="
              lecturer?.profile && lecturer.profile.groups.positions?.length
            "
            class="text-lg"
          >
            {{ lecturer.profile.groups.positions.join(", ") }}
          </div>
        </div>
      </div>
      <!-- Tabs Navigation -->
      <nav class="tabs bg-white border-t">
        <ul class="flex justify-center">
          <li
            v-for="tab in tabs"
            :key="tab.name"
            :class="{ active: activeTab === tab.name }"
            @click="activeTab = tab.name"
            class="px-4 py-2 cursor-pointer flex items-center space-x-1"
          >
            <span>{{ translate(tab.label) }}</span>
            <span
              v-if="tab.name === 'events'"
              class="bg-gray-400 text-white rounded px-2 py-0.5 text-xs"
            >
              {{ eventsCount }}
            </span>
            <span
              v-if="tab.name === 'reviews'"
              class="bg-gray-400 text-white rounded px-2 py-0.5 text-xs"
            >
              {{ reviewsCount }}
            </span>
          </li>
        </ul>
      </nav>
    </header>

    <!-- Main Content -->
    <main class="flex flex-col flex-grow container mx-auto p-6 space-y-8">
      <!-- Info Tab -->
      <section v-if="activeTab === 'info'" class="space-y-8">
        <div
          v-if="lecturer?.profile?.excerpt"
          v-html="lecturer.profile.excerpt"
          class="prose max-w-none text-lg py-4"
        ></div>

        <!-- Specializations -->
        <div
          v-if="
            lecturer?.profile && lecturer.profile.groups.specializations?.length
          "
          class="mt-6"
        >
          <h4 class="text-xl font-semibold mb-2 text-gray-600">
            {{ translate("lecturers.profile.specializations") }}:
          </h4>
          <div class="flex flex-wrap gap-2">
            <span
              v-for="spec in lecturer.profile.groups.specializations"
              :key="spec"
              class="inline-tag px-3 py-1 bg-gray-100 rounded text-sm"
            >
              {{ spec }}
            </span>
          </div>
        </div>

        <!-- Services -->
        <div
          v-if="lecturer?.profile && lecturer.profile.groups.services?.length"
          class="mt-6"
        >
          <h4 class="text-xl font-semibold mb-2 text-gray-600">
            {{ translate("lecturers.profile.services") }}:
          </h4>
          <div class="flex flex-wrap gap-2">
            <span
              v-for="service in lecturer.profile.groups.services"
              :key="service"
              class="inline-tag px-3 py-1 bg-gray-100 rounded text-sm"
            >
              {{ service }}
            </span>
          </div>
        </div>

        <!-- Brand Cards -->
        <div v-if="normalizedBrands.length" class="mt-6 border-t-2 pt-4">
          <h4 class="text-xl font-semibold mb-4 text-gray-600 px-4">
            {{ translate("lecturers.profile.brands") }}:
          </h4>
          <div class="flex flex-wrap gap-6">
            <div
              v-for="brand in normalizedBrands"
              :key="brand.data.id"
              class="brand-card bg-white shadow rounded overflow-hidden w-full md:w-80"
            >
              <!-- Brand Cover with Logo Overlay -->
              <div class="relative h-32">
                <div
                  class="w-full h-full"
                  :style="{
                    backgroundImage:
                      'url(' + brand.data.images.cover.large + ')',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }"
                ></div>
                <!-- Logo over cover on bottom left -->
                <div
                  v-if="
                    brand.data.images.icon.small ||
                    brand.data.images.logo.small.dark
                  "
                  class="relative"
                >
                  <img
                    v-if="brand.data.images.icon.small"
                    :src="brand.data.images.icon.small"
                    alt="Brand Icon"
                    class="absolute bottom-1 left-2 w-16 h-16 object-contain border-2 bg-white rounded-full"
                  />
                  <img
                    v-else-if="brand.data.images.logo.small.dark"
                    :src="brand.data.images.logo.small.dark"
                    alt="Brand Logo"
                    class="absolute bottom-1 left-2 w-16 h-16 object-contain border-2 bg-white rounded-full"
                  />
                </div>
              </div>
              <!-- Brand Information -->
              <div class="p-4 space-y-3">
                <div class="flex items-center justify-between">
                  <h5 class="font-bold text-lg">{{ brand.data.name }}</h5>
                  <div
                    v-if="
                      brand.data.contributors && brand.data.contributors.length
                    "
                    class="flex items-center space-x-1"
                  >
                    <img
                      v-for="contrib in brand.data.contributors"
                      :key="contrib.name"
                      :src="contrib.avatar"
                      :alt="contrib.name"
                      class="w-6 h-6 rounded-full object-cover"
                    />
                  </div>
                </div>
                <p class="text-sm text-gray-500">
                  {{
                    translate("lecturers.profile.brand.position", {
                      position: brand.role,
                    })
                  }}
                </p>
                <p
                  class="text-sm text-gray-700"
                  v-html="brand.data.description"
                ></p>
                <div
                  class="flex items-center space-x-4"
                  v-if="brand.data.url.homepage || brand.data.support.email"
                >
                  <a
                    v-if="brand.data.url.homepage"
                    :href="brand.data.url.homepage"
                    target="_blank"
                    class="inline-block text-blue-500 hover:underline text-sm"
                  >
                    {{ translate("lecturers.profile.brand.website") }}
                  </a>
                  <a
                    v-if="brand.data.support.email"
                    :href="brand.data.support.email"
                    target="_blank"
                    class="inline-block text-blue-500 hover:underline text-sm"
                  >
                    {{ translate("lecturers.profile.brand.email") }}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Events Tab -->
      <section v-if="activeTab === 'events'">
        <div
          class="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4"
        >
          <template v-if="eventsLoading">
            <SkeletonCard v-for="n in 6" :key="'skeleton-' + n" />
          </template>
          <template v-else-if="events && events.length">
            <EventCard v-for="event in events" :key="event.id" :event="event" />
          </template>
          <template v-else>
            <div class="col-span-full text-center py-20">
              <p>{{ translate("lecturers.profile.noEvents") }}</p>
            </div>
          </template>
        </div>
        <!-- Pagination -->
        <div
          v-if="pagination.totalPages.value > 1"
          class="flex justify-center py-10"
        >
          <ApiPagination
            :total-pages="pagination.totalPages.value"
            :current-page="pagination.currentPage.value"
            :has-more-pages="pagination.hasMorePages.value"
            @pagechanged="handlePageChange"
          />
        </div>
      </section>

      <!-- Reviews Tab -->
      <section v-if="activeTab === 'reviews'">
        <div v-if="reviewsLoading" class="text-center py-10">
          {{ translate("lecturers.profile.loadingReviews") }}
        </div>
        <div v-else-if="reviewsError" class="text-center py-10 text-red-500">
          {{ reviewsError }}
        </div>
        <!-- Reviews Grid -->
        <div
          v-else
          class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 pt-6"
        >
          <div
            v-for="review in lecturerReviews"
            :key="review.id"
            class="bg-white shadow-sm rounded-lg p-4 flex flex-col space-y-3 review-card"
          >
            <!-- Top row: avatar, name, position/date -->
            <div class="flex items-center space-x-3">
              <img
                v-if="review.avatar"
                :src="review.avatar"
                alt="Avatar"
                class="w-12 h-12 rounded-full object-cover"
              />
              <div>
                <h3 class="text-gray-900 font-semibold text-sm">
                  {{ review.name }}
                </h3>
                <p class="text-xs text-gray-500">
                  {{
                    review.position
                      ? review.position
                      : localizedDate(review.date)
                  }}
                </p>
              </div>
            </div>

            <!-- Review text -->
            <div
              v-if="review.review"
              class="text-gray-700 text-sm leading-relaxed"
            >
              {{ review.review }}
            </div>

            <!-- Bottom row: event info + rating -->
            <div
              class="flex items-center justify-between border-t border-gray-200 pt-2 mt-auto"
            >
              <div class="flex items-center space-x-2">
                <img
                  v-if="review.event.thumbnail"
                  :src="review.event.thumbnail"
                  alt="Event thumbnail"
                  class="w-6 h-6 rounded-full object-cover"
                />
                <a
                  :href="review.event.url"
                  class="text-xs text-gray-400 hover:underline"
                >
                  {{ review.event.name }}
                </a>
              </div>
              <div class="flex items-center">
                <FontAwesomeIcon
                  v-for="n in 5"
                  :key="n"
                  :icon="getStarIcon(review.overall / 2, n)"
                  class="text-yellow-400 text-md"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Contact Tab -->
      <section v-if="activeTab === 'contact'">
        <div class="bg-white rounded-lg shadow p-8">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
            <!-- Lecturer Contact Details -->
            <div class="space-y-6">
              <h3 class="text-2xl font-bold text-gray-800">
                {{ translate("lecturers.profile.contactInfo") }}
              </h3>
              <div v-if="lecturer?.phone" class="flex items-center space-x-2">
                <span class="font-semibold text-gray-700"
                  >{{ translate("lecturers.profile.phone") }}:</span
                >
                <a
                  :href="`tel:${lecturer.phone}`"
                  class="text-blue-500 hover:underline"
                >
                  {{ lecturer.phone }}
                </a>
              </div>
              <div v-if="lecturer?.email" class="flex items-center space-x-2">
                <span class="font-semibold text-gray-700"
                  >{{ translate("lecturers.profile.email") }}:</span
                >
                <a
                  :href="`mailto:${lecturer.email}`"
                  class="text-blue-500 hover:underline"
                >
                  {{ lecturer.email }}
                </a>
              </div>
              <div
                v-if="lecturer?.profile?.social?.website"
                class="flex items-center space-x-2"
              >
                <span class="font-semibold text-gray-700"
                  >{{ translate("lecturers.profile.website") }}:</span
                >
                <a
                  :href="lecturer.profile.social.website"
                  target="_blank"
                  class="text-blue-500 hover:underline"
                >
                  {{ lecturer.profile.social.website }}
                </a>
              </div>
              <div v-if="lecturer?.profile?.social" class="space-y-2">
                <span class="font-semibold text-gray-700"
                  >{{ translate("lecturers.profile.social") }}:</span
                >
                <div class="flex space-x-4">
                  <a
                    v-if="lecturer.profile.social.facebook"
                    :href="lecturer.profile.social.facebook"
                    target="_blank"
                  >
                    <FontAwesomeIcon
                      :icon="faFacebook"
                      class="text-blue-600 w-6 h-6"
                    />
                  </a>
                  <a
                    v-if="lecturer.profile.social.twitter"
                    :href="lecturer.profile.social.twitter"
                    target="_blank"
                  >
                    <FontAwesomeIcon
                      :icon="faTwitter"
                      class="text-blue-400 w-6 h-6"
                    />
                  </a>
                  <a
                    v-if="lecturer.profile.social.linkedin"
                    :href="lecturer.profile.social.linkedin"
                    target="_blank"
                  >
                    <FontAwesomeIcon
                      :icon="faLinkedin"
                      class="text-blue-700 w-6 h-6"
                    />
                  </a>
                  <a
                    v-if="lecturer.profile.social.youtube"
                    :href="lecturer.profile.social.youtube"
                    target="_blank"
                  >
                    <FontAwesomeIcon
                      :icon="faYoutube"
                      class="text-red-600 w-6 h-6"
                    />
                  </a>
                </div>
              </div>
            </div>

            <!-- Contact Form -->
            <div>
              <h3 class="text-2xl font-bold text-gray-800 mb-4">
                {{ translate("lecturers.profile.getInTouch") }}
              </h3>
              <form @submit.prevent="submitContactForm" class="space-y-4">
                <div>
                  <label class="block text-gray-700">{{
                    translate("lecturers.profile.yourName")
                  }}</label>
                  <input
                    v-model="contactForm.name"
                    type="text"
                    class="w-full border border-gray-300 rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label class="block text-gray-700">{{
                    translate("lecturers.profile.yourEmail")
                  }}</label>
                  <input
                    v-model="contactForm.email"
                    type="email"
                    class="w-full border border-gray-300 rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label class="block text-gray-700">{{
                    translate("lecturers.profile.message")
                  }}</label>
                  <textarea
                    v-model="contactForm.message"
                    class="w-full border border-gray-300 rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows="4"
                    required
                  ></textarea>
                </div>
                <button
                  type="submit"
                  class="bg-blue-500 hover:bg-blue-600 transition text-white font-semibold rounded px-6 py-2"
                >
                  {{ translate("lecturers.profile.send") }}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </main>

    <!-- Footer -->
    <FooterModule
      v-if="lecturer && lecturer.profile"
      :getColor="getColor"
      :isSidebarOpen="false"
      :eventType="eventType"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { useRoute } from "vue-router";
import { useLecturer } from "@/composables/useLecturer";
import { listEvents } from "@/composables/listEvents";
import { usePagination } from "@/composables/usePagination";
import { useLecturerReview } from "@/composables/useLecturerReview";
import EventCard from "@/components/default/card/EventCard.vue";
import SkeletonCard from "@/components/default/card/SkeletonCard.vue";
import FooterModule from "@/components/default/module/main/FooterModule.vue";
import ApiPagination from "@/components/general/ApiPagination.vue";
import { getColorClass } from "@/utils/helper";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { faStar, faStarHalfAlt } from "@fortawesome/free-solid-svg-icons";
import { faStar as faStarEmpty } from "@fortawesome/free-regular-svg-icons";
import {
  faFacebook,
  faTwitter,
  faLinkedin,
  faYoutube,
} from "@fortawesome/free-brands-svg-icons";
import { format } from "date-fns";
import { cs } from "date-fns/locale";
import { translate } from "@/utils/translate";
import { getEventType } from "@/utils/helper";

// Extract lecturer slug from the route
const route = useRoute();
const activeTab = ref("info");
const lecturerSlug = route.params.lecturerSlug;
console.log("Lecturer slug:", lecturerSlug);

// 1) Fetch lecturer details on mount
const { lecturer, fetchLecturer } = useLecturer();
const eventType = getEventType();
onMounted(() => {
  fetchLecturer(lecturerSlug);
});

// 2) Setup events but load them only when the "Events" tab is clicked
const perPage = window.vueAppConfig.settings.list_limit || 6;
const {
  events,
  loading: eventsLoading,
  fetchEvents,
  totalItems,
} = listEvents();
const pagination = usePagination(totalItems, perPage);

// Keep track if we've already loaded events to avoid re-fetching
const eventsLoaded = ref(false);
function handlePageChange(newPage) {
  if (lecturer.value && lecturer.value.id) {
    fetchEvents(null, newPage, perPage, false, "", false, [lecturer.value.id]);
  }
}

// 3) Only fetch events when the user selects the "Events" tab
watch(
  () => activeTab.value,
  (newTab) => {
    if (
      newTab === "events" &&
      !eventsLoaded.value &&
      lecturer.value &&
      lecturer.value.id
    ) {
      eventsLoaded.value = true;
      fetchEvents(null, 1, perPage, false, "", false, [lecturer.value.id]);
    }
  }
);

// 4) Determine if there's a cover image
const hasCover = computed(() => {
  return (
    lecturer.value &&
    lecturer.value.profile &&
    lecturer.value.profile.images &&
    lecturer.value.profile.images.cover &&
    lecturer.value.profile.images.cover.big
  );
});
const heroBackgroundStyle = computed(() => {
  if (hasCover.value) {
    return {
      backgroundImage: `url(${lecturer.value.profile.images.cover.big})`,
      backgroundSize: "cover",
      backgroundPosition: "center",
    };
  }
  return {};
});

// 5) Fallback getColor function for FooterModule
const getColor = (shade = 500, prefix = "text") => {
  if (lecturer.value && lecturer.value.profile) {
    return getColorClass(lecturer.value.profile, shade, prefix);
  }
  return "";
};

// 6) Tabs and Contact Form (Contact tab now includes contact info and a form)
const tabs = [
  { name: "info", label: "lecturers.profile.info" },
  { name: "events", label: "lecturers.profile.events" },
  { name: "reviews", label: "lecturers.profile.reviews" },
  { name: "contact", label: "lecturers.profile.contact" },
];

const contactForm = ref({
  name: "",
  email: "",
  message: "",
});

function submitContactForm() {
  console.log("Contact Form Submitted", contactForm.value);
  contactForm.value = { name: "", email: "", message: "" };
}

// 7) Setup lecturer review via the new composable
const {
  lecturerReviews,
  loading: reviewsLoading,
  error: reviewsError,
  fetchLecturerReview,
} = useLecturerReview();

// Only load reviews on the "Reviews" tab
watch(activeTab, (newTab) => {
  if (newTab === "reviews" && lecturer.value && lecturer.value.id) {
    fetchLecturerReview(lecturer.value.id);
  }
});

// 8) Helper: Return the appropriate star icon for a given rating value.
function getStarIcon(rating, starIndex) {
  if (starIndex <= rating) {
    return faStar;
  } else if (starIndex - 0.5 <= rating) {
    return faStarHalfAlt;
  } else {
    return faStarEmpty;
  }
}

// 9) Helper: Format dates using date-fns with Czech locale.
function localizedDate(dateString) {
  return format(new Date(dateString), "PPP", { locale: cs });
}

// 10) Computed badges for tabs
const eventsCount = computed(() => {
  return lecturer.value?.events_count || 0;
});
const reviewsCount = computed(() => {
  if (!lecturer.value || !lecturer.value.events) return 0;
  return lecturer.value.events.reduce((total, event) => {
    return total + (event.rating?.count || 0);
  }, 0);
});

// 11) Normalize brands data into an array
const normalizedBrands = computed(() => {
  if (lecturer.value && lecturer.value.brands) {
    return Array.isArray(lecturer.value.brands)
      ? lecturer.value.brands
      : [lecturer.value.brands];
  }
  return [];
});
</script>

<style scoped>
.facebook-header {
  background-color: #fff;
}
.cover-image {
  height: 300px;
  position: relative;
}
.cover-image::after {
  content: "";
  position: absolute;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
}
.profile-info {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  padding: 1rem;
}
.tabs ul {
  list-style: none;
  display: flex;
  margin: 0;
  padding: 0;
  border-bottom: 1px solid #ddd;
}
.tabs li {
  cursor: pointer;
}
.tabs li.active {
  border-bottom: 3px solid #1877f2;
  color: #1877f2;
}
.review-card .fa-star,
.review-card .fa-star-half-alt,
.review-card .fa-star-o {
  margin-left: 2px;
}
.brand-card {
  margin-bottom: 1.5rem;
}
</style>
